import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  Save,
  Eye,
  EyeOff,
  AlertCircle,
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Lock,
  Briefcase,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  save_update_staff_res,
  get_all_staff_captain,
  APL_LINK,
} from "../ServiceConnection/serviceconnection";
import {
  handleEmailChange,
  handleNumbersChange,
  handleAphabetsChange,
  handlePasswordChange,
  calculateMaxDate,
  calculateMinJoinDate,
  calculateMaxJoinDate,
} from "../CommonJquery/CommonJquery";
import Loader from "../components/layout/Loader";

const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

export default function AddEditStaff() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { toast } = useToast();

  const staffId = params.id || location.pathname.split("/")[2];
  const isEditMode = !!staffId;

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [designations, setDesignations] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // Form data
  const [formData, setFormData] = useState({
    admin_name: "",
    admin_dob_date: "",
    admin_gender: "",
    admin_email: "",
    admin_mobile_no: "",
    admin_manual_adrress: "",
    admin_city: "",
    admin_state: "",
    admin_country: "",
    admin_join_date: "",
    admin_designation: "",
    admin_password: "",
    admin_confirm_password: "",
  });

  const [editorDataMainID, setEditorDataMainID] = useState("0");
  const [editOldImageData, setEditOldImageData] = useState("");

  // Clear field error
  const clearFieldError = useCallback((field) => {
    setErrors((prev) => {
      const { [field]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // Handle field change
  const handleFieldChange = useCallback(
    (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      clearFieldError(field);
    },
    [clearFieldError],
  );

  // Fetch staff data for edit
  const fetchStaffData = useCallback(
    async (callId) => {
      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("start_date", "");
      fd.append("end_date", "");
      fd.append("flag", "3");
      fd.append("call_id", callId);

      try {
        const response = await server_post_data(get_all_staff_captain, fd);

        if (!response.data.error) {
          if (response.data.message.data_staff.length > 0) {
            const staffData = response.data.message.data_staff[0];

            setFormData({
              admin_name: staffData.admin_name || "",
              admin_dob_date: staffData.admin_dob_date || "",
              admin_gender: staffData.admin_gender || "",
              admin_email: staffData.admin_email || "",
              admin_mobile_no: staffData.admin_mobile_no || "",
              admin_manual_adrress: staffData.admin_manual_adrress || "",
              admin_city: staffData.admin_city || "",
              admin_state: staffData.admin_state || "",
              admin_country: staffData.admin_country || "",
              admin_join_date: staffData.admin_join_date || "",
              admin_designation: staffData.admin_designation || "",
              admin_password: staffData.admin_password || "",
              admin_confirm_password: staffData.admin_password || "",
            });

            setEditorDataMainID(staffData.primary_id);
            setEditOldImageData(staffData.admin_image);
          }
          setDesignations(response.data.message.data_designation || []);
        }
      } catch (error) {
        console.error("Error fetching staff data:", error);
        toast({
          title: "Error",
          description: "Failed to load staff data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [toast],
  );

  // Fetch designations only (for add mode)
  const fetchDesignations = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("start_date", "");
    fd.append("end_date", "");
    fd.append("flag", "3");
    fd.append("call_id", "0");

    try {
      const response = await server_post_data(get_all_staff_captain, fd);

      if (!response.data.error) {
        setDesignations(response.data.message.data_designation || []);
      }
    } catch (error) {
      console.error("Error fetching designations:", error);
    } finally {
      setShowLoaderAdmin(false);
    }
  }, []);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    try {
      const input = document.getElementById("searchInput");
      if (!input || !window.google) return;

      const autocomplete = new window.google.maps.places.Autocomplete(input);

      autocomplete.addListener("place_changed", function () {
        const place = autocomplete.getPlace();
        const addressComponents = place.address_components || [];
        const formattedAddress = place.formatted_address || "";

        let city = "";
        let state = "";
        let country = "";
        let tehsil = "";

        addressComponents.forEach((component) => {
          const types = component.types;
          if (types.includes("administrative_area_level_1")) {
            state = component.long_name;
          } else if (types.includes("country")) {
            country = component.long_name;
          } else if (types.includes("administrative_area_level_2")) {
            city = component.long_name;
          } else if (types.includes("locality")) {
            tehsil = component.long_name;
          }
        });

        if (tehsil) city = tehsil;

        setFormData((prev) => ({
          ...prev,
          admin_manual_adrress: formattedAddress,
          admin_city: city,
          admin_state: state,
          admin_country: country,
        }));
      });
    } catch (error) {
      console.log("Google Maps not loaded:", error);
    }
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.admin_name.trim()) {
      newErrors.admin_name = "Name is required";
    }

    if (!formData.admin_dob_date) {
      newErrors.admin_dob_date = "Date of birth is required";
    }

    if (!formData.admin_gender) {
      newErrors.admin_gender = "Gender is required";
    }

    if (!formData.admin_email.trim()) {
      newErrors.admin_email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.admin_email)) {
      newErrors.admin_email = "Invalid email format";
    }

    if (!formData.admin_mobile_no.trim()) {
      newErrors.admin_mobile_no = "Mobile number is required";
    } else if (formData.admin_mobile_no.length < 10) {
      newErrors.admin_mobile_no = "Mobile number must be 10 digits";
    }

    if (!formData.admin_manual_adrress.trim()) {
      newErrors.admin_manual_adrress = "Address is required";
    }

    if (!formData.admin_join_date) {
      newErrors.admin_join_date = "Joining date is required";
    }

    if (!formData.admin_designation) {
      newErrors.admin_designation = "Designation is required";
    }

    if (!formData.admin_password.trim()) {
      newErrors.admin_password = "Password is required";
    } else if (formData.admin_password.length < 3) {
      newErrors.admin_password = "Password must be at least 3 characters";
    }

    if (!formData.admin_confirm_password.trim()) {
      newErrors.admin_confirm_password = "Confirm password is required";
    } else if (formData.admin_password !== formData.admin_confirm_password) {
      newErrors.admin_confirm_password = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields correctly",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("main_id", editorDataMainID);
    fd.append("old_image_link", editOldImageData);

    Object.keys(formData).forEach((key) => {
      fd.append(key, formData[key]);
    });

    try {
      const response = await server_post_data(save_update_staff_res, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to save staff",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: isEditMode
            ? "Staff updated successfully"
            : "Staff created successfully",
        });
        navigate("/staff-management");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [
    validateForm,
    formData,
    editorDataMainID,
    editOldImageData,
    isEditMode,
    toast,
    navigate,
  ]);

  // Initial load
  useEffect(() => {
    if (staffId) {
      fetchStaffData(staffId);
    } else {
      fetchDesignations();
    }
  }, [staffId, fetchStaffData, fetchDesignations]);

  return (
    <MainLayout>
      <Header
        title={isEditMode ? "Edit Staff Member" : "Add New Staff Member"}
        subtitle={
          isEditMode
            ? "Update staff member details"
            : "Create a new staff member for your restaurant"
        }
      />

      <div className="p-6 space-y-6">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => navigate("/staff-management")}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Staff
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal Details */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Personal Details
            </h3>

            <div className="space-y-4">
              {/* Name */}
              <div className="space-y-2">
                <Label>
                  Full Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  placeholder="Enter full name"
                  value={formData.admin_name}
                  onChange={(e) => {
                    handleAphabetsChange(e);
                    handleFieldChange("admin_name", e.target.value);
                  }}
                  className={errors.admin_name ? "border-destructive" : ""}
                  maxLength={75}
                  minLength={3}
                />
                {errors.admin_name && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.admin_name}
                  </p>
                )}
              </div>

              {/* Date of Birth */}
              <div className="space-y-2">
                <Label>
                  Date of Birth <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="date"
                  value={formData.admin_dob_date}
                  onChange={(e) =>
                    handleFieldChange("admin_dob_date", e.target.value)
                  }
                  className={errors.admin_dob_date ? "border-destructive" : ""}
                  max={calculateMaxDate()}
                />
                {errors.admin_dob_date && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.admin_dob_date}
                  </p>
                )}
              </div>

              {/* Gender */}
              <div className="space-y-2">
                <Label>
                  Gender <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.admin_gender}
                  onValueChange={(value) =>
                    handleFieldChange("admin_gender", value)
                  }
                >
                  <SelectTrigger
                    className={errors.admin_gender ? "border-destructive" : ""}
                  >
                    <SelectValue placeholder="Select Gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDER_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.admin_gender && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.admin_gender}
                  </p>
                )}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label>
                  Email Address <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="email"
                  placeholder="name@example.com"
                  value={formData.admin_email}
                  onChange={(e) => {
                    handleEmailChange(e);
                    handleFieldChange("admin_email", e.target.value);
                  }}
                  className={errors.admin_email ? "border-destructive" : ""}
                  maxLength={72}
                  minLength={4}
                />
                {errors.admin_email && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.admin_email}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  This will be used as login username
                </p>
              </div>

              {/* Mobile Number */}
              <div className="space-y-2">
                <Label>
                  Mobile Number <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="text"
                  placeholder="10-digit mobile number"
                  value={formData.admin_mobile_no}
                  onChange={(e) => {
                    handleNumbersChange(e);
                    handleFieldChange("admin_mobile_no", e.target.value);
                  }}
                  className={errors.admin_mobile_no ? "border-destructive" : ""}
                  maxLength={10}
                  minLength={10}
                />
                {errors.admin_mobile_no && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.admin_mobile_no}
                  </p>
                )}
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label>
                  Permanent Address <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="searchInput"
                  placeholder="Enter permanent address"
                  value={formData.admin_manual_adrress}
                  onChange={(e) =>
                    handleFieldChange("admin_manual_adrress", e.target.value)
                  }
                  className={
                    errors.admin_manual_adrress ? "border-destructive" : ""
                  }
                  rows={4}
                  maxLength={250}
                  minLength={5}
                />
                {errors.admin_manual_adrress && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.admin_manual_adrress}
                  </p>
                )}
              </div>

              {/* Hidden fields for Google Places */}
              <div className="hidden">
                <Input
                  id="admin_city"
                  value={formData.admin_city}
                  onChange={(e) =>
                    handleFieldChange("admin_city", e.target.value)
                  }
                />
                <Input
                  id="admin_state"
                  value={formData.admin_state}
                  onChange={(e) =>
                    handleFieldChange("admin_state", e.target.value)
                  }
                />
                <Input
                  id="admin_country"
                  value={formData.admin_country}
                  onChange={(e) =>
                    handleFieldChange("admin_country", e.target.value)
                  }
                />
              </div>
            </div>
          </div>

          {/* Employee Details */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Employee Details
            </h3>

            <div className="space-y-4">
              {/* Joining Date */}
              <div className="space-y-2">
                <Label>
                  Joining Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="date"
                  value={formData.admin_join_date}
                  onChange={(e) =>
                    handleFieldChange("admin_join_date", e.target.value)
                  }
                  className={errors.admin_join_date ? "border-destructive" : ""}
                  max={calculateMaxJoinDate()}
                  min={calculateMinJoinDate()}
                />
                {errors.admin_join_date && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.admin_join_date}
                  </p>
                )}
              </div>

              {/* Designation */}
              <div className="space-y-2">
                <Label>
                  Designation <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.admin_designation.toString()}
                  onValueChange={(value) =>
                    handleFieldChange("admin_designation", value)
                  }
                >
                  <SelectTrigger
                    className={
                      errors.admin_designation ? "border-destructive" : ""
                    }
                  >
                    <SelectValue placeholder="Select Designation" />
                  </SelectTrigger>
                  <SelectContent>
                    {designations.map((designation) => (
                      <SelectItem
                        key={designation.primary_id}
                        value={designation.primary_id.toString()}
                      >
                        {designation.designation_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.admin_designation && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.admin_designation}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label>
                  Create Password <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={formData.admin_password}
                    onChange={(e) => {
                      handlePasswordChange(e);
                      handleFieldChange("admin_password", e.target.value);
                    }}
                    className={
                      errors.admin_password ? "border-destructive" : ""
                    }
                    maxLength={40}
                    minLength={3}
                    onPaste={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {errors.admin_password && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.admin_password}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label>
                  Confirm Password <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm password"
                    value={formData.admin_confirm_password}
                    onChange={(e) => {
                      handlePasswordChange(e);
                      handleFieldChange(
                        "admin_confirm_password",
                        e.target.value,
                      );
                    }}
                    className={
                      errors.admin_confirm_password ? "border-destructive" : ""
                    }
                    maxLength={40}
                    minLength={3}
                    onPaste={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {errors.admin_confirm_password && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.admin_confirm_password}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/staff-management")}
            disabled={showLoaderAdmin}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={showLoaderAdmin}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            {showLoaderAdmin ? (
              "Saving..."
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditMode ? "Update Staff" : "Create Staff"}
              </>
            )}
          </Button>
        </div>
      </div>
      {showLoaderAdmin && <Loader />}
    </MainLayout>
  );
}
